import { useCallback, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from '../components/Toast';

const FUNCTIONS_BASE = 'https://us-central1-sistema-bks.cloudfunctions.net';

/**
 * Shared Starter->Pro upgrade flow (preview + immediate charge + Firestore
 * sync wait), used by both /addon (ContratarAddon.js) and /billing
 * (Billing.jsx) so the two entry points never drift out of sync.
 *
 * @param {{mode: string, orgId?: string}} context - from useOrganization()
 */
// onSynced (opcional): si se pasa, se llama en vez de window.location.reload()
// cuando el webhook confirma el cambio — el consumidor decide cómo refrescar
// (ej. /billing tiene onSnapshot y solo re-fetchea Stripe). Sin onSynced, cae
// al reload (comportamiento actual de /addon, que no tiene listener).
export default function useUpgradeToPro(context, { onSynced } = {}) {
  const [prorrateo, setProrrateo] = useState(null); // { monto, moneda, prorationDate }
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { t } = useTranslation('addons');
  const auth = getAuth();
  const db = getFirestore();

  const orgPayload = useCallback(() => (
    context.mode === 'organization' && context.orgId ? { organizationId: context.orgId } : {}
  ), [context.mode, context.orgId]);

  const calcularPreview = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return null;

    setLoadingPreview(true);
    try {
      const idToken = await user.getIdToken();
      const res = await axios.post(
        `${FUNCTIONS_BASE}/calcularProrrateoUpgrade`,
        orgPayload(),
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      if (res.data.success) {
        const data = {
          monto: res.data.monto,
          moneda: res.data.currency.toUpperCase(),
          prorationDate: res.data.prorationDate,
        };
        setProrrateo(data);
        return data;
      }
      return null;
    } catch (error) {
      console.error('❌ Error calculando prorrateo:', error);
      setProrrateo(null);
      return null;
    } finally {
      setLoadingPreview(false);
    }
  }, [auth, orgPayload]);

  // Espera a que el webhook de Stripe (customer.subscription.updated) confirme
  // el cambio de plan en Firestore, en vez de recargar la página a ciegas.
  // organizations/{orgId} / clientes/{uid} es escrito únicamente por el
  // webhook (stripeWebhook.js) — este flujo solo dispara la mutación en
  // Stripe. El webhook confirma normalmente en <1s; el timeout es solo un
  // fallback de UI, no un error — el pago ya se confirmó con la respuesta
  // 200 de upgradeToPro.
  const waitForPlanSync = useCallback((targetPlan) => {
    return new Promise((resolve) => {
      const user = auth.currentUser;
      const docRef = context.mode === 'organization' && context.orgId
        ? doc(db, 'organizations', context.orgId)
        : doc(db, 'clientes', user.uid);

      let settled = false;

      const finish = (synced) => {
        if (settled) return;
        settled = true;
        unsubscribe();
        clearTimeout(timeoutId);
        toast[synced ? 'success' : 'info'](t(synced ? 'upgradeSynced' : 'upgradeSyncDelayed'));
        if (onSynced) onSynced(); else window.location.reload();
        resolve();
      };

      const unsubscribe = onSnapshot(docRef, (snap) => {
        const data = snap.data();
        const rawPlan = data?.planId || data?.plan;
        const currentPlan = (rawPlan?.id || rawPlan || '').toString().toLowerCase();
        if (currentPlan === targetPlan) finish(true);
      }, () => finish(false));

      const timeoutId = setTimeout(() => finish(false), 7000);
    });
  }, [auth, context.mode, context.orgId, db, t, onSynced]);

  const confirmarUpgrade = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      toast.error(t('loginRequired'));
      return;
    }

    setProcessing(true);
    try {
      const idToken = await user.getIdToken();
      const payload = { ...orgPayload() };
      if (prorrateo?.prorationDate) {
        payload.prorationDate = prorrateo.prorationDate;
      }

      const res = await axios.post(
        `${FUNCTIONS_BASE}/upgradeToPro`,
        payload,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      const monto = res.data?.prorrateo?.monto || prorrateo?.monto;
      const moneda = res.data?.prorrateo?.moneda || prorrateo?.moneda;
      toast.success(t('upgradeComplete', { amount: monto || 'N/A', currency: moneda || '' }));

      await waitForPlanSync('pro');
    } catch (error) {
      console.error('❌ Error al hacer upgrade a Pro:', error);
      const serverMsg = error.response?.data?.error;
      toast.error(serverMsg || t('paymentError'));
    } finally {
      setProcessing(false);
    }
  }, [auth, orgPayload, prorrateo, waitForPlanSync, t]);

  return { prorrateo, loadingPreview, processing, calcularPreview, confirmarUpgrade };
}
